import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, AlertCircle, RefreshCw, PackageOpen } from 'lucide-react';
import Navigation from '../../components/Navigation';
import { supabase } from '../../lib/supabase';

import ArchiveTabBar from './components/ArchiveTabBar';
import ArchiveOfferCard from './components/ArchiveOfferCard';
import ArchiveFilters from './components/ArchiveFilters';
import HistoryModal from './components/HistoryModal';

function applyFiltersAndSort(offers, filters) {
  let result = [...(offers || [])];

  if (filters?.search) {
    const q = filters?.search?.toLowerCase();
    result = result?.filter(
      (o) =>
        o?.voucherName?.toLowerCase()?.includes(q) ||
        o?.voucherSymbol?.toLowerCase()?.includes(q) ||
        o?.category?.toLowerCase()?.includes(q)
    );
  }

  if (filters?.wishType) {
    result = result?.filter((o) => o?.wishType === filters?.wishType);
  }

  switch (filters?.sortBy) {
    case 'oldest':
      result?.sort((a, b) => new Date(a?.createdAt) - new Date(b?.createdAt));
      break;
    case 'price_asc':
      result?.sort((a, b) => (a?.maxPrice ?? 0) - (b?.maxPrice ?? 0));
      break;
    case 'price_desc':
      result?.sort((a, b) => (b?.maxPrice ?? 0) - (a?.maxPrice ?? 0));
      break;
    default:
      result?.sort((a, b) => new Date(b?.createdAt) - new Date(a?.createdAt));
  }

  return result;
}

export default function MyOffersArchive() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('archived');
  const [allOffers, setAllOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [filters, setFilters] = useState({ search: '', wishType: '', sortBy: 'newest' });
  const [restoringId, setRestoringId] = useState(null);
  const [relistingId, setRelistingId] = useState(null);
  const [historyOffer, setHistoryOffer] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    initUser();
  }, []);

  const initUser = async () => {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (user) {
      setCurrentUserId(user?.id);
      fetchOffers(user?.id);
    } else {
      setError('Morate biti prijavljeni da biste videli arhivu oglasa.');
      setLoading(false);
    }
  };

  const fetchOffers = useCallback(async (userId) => {
    setLoading(true);
    setError('');
    try {
      const uid = userId || currentUserId;
      if (!uid) return;

      const { data, error: err } = await supabase
        ?.from('marketplace_wishes')
        ?.select('*')
        ?.eq('user_id', uid)
        ?.in('offer_status', ['archived', 'expired', 'completed'])
        ?.order('created_at', { ascending: false });

      if (err) throw err;

      const mapped = (data || [])?.map((r) => ({
        id: r?.id,
        userId: r?.user_id,
        wishType: r?.wish_type,
        voucherName: r?.voucher_name,
        voucherSymbol: r?.voucher_symbol,
        wishTitle: r?.wish_title,
        tokenQuantity: r?.token_quantity,
        maxPrice: r?.max_price,
        currency: r?.currency,
        description: r?.description,
        specialConditions: r?.special_conditions,
        exchangeWishes: r?.exchange_wishes,
        expiryDate: r?.expiry_date,
        category: r?.category,
        images: r?.images || [],
        logoUrl: r?.logo_url,
        isActive: r?.is_active,
        offerStatus: r?.offer_status,
        createdAt: r?.created_at,
        updatedAt: r?.updated_at,
      }));

      setAllOffers(mapped);
    } catch (e) {
      setError('Greška pri učitavanju arhive: ' + e?.message);
    } finally {
      setLoading(false);
    }
  }, [currentUserId]);

  const handleRestore = async (offerId) => {
    setRestoringId(offerId);
    try {
      const { error: err } = await supabase
        ?.from('marketplace_wishes')
        ?.update({ is_active: true, offer_status: 'active' })
        ?.eq('id', offerId)
        ?.eq('user_id', currentUserId);
      if (err) throw err;
      setAllOffers((prev) => prev?.filter((o) => o?.id !== offerId));
      showSuccess('Oglas je uspešno vraćen na oglasnu tablu!');
    } catch (e) {
      setError('Greška pri vraćanju oglasa: ' + e?.message);
    } finally {
      setRestoringId(null);
    }
  };

  const handleRelist = (offer) => {
    navigate('/objavi-ponudu-form', {
      state: {
        prefill: {
          voucherName: offer?.voucherName,
          voucherSymbol: offer?.voucherSymbol,
          wishType: offer?.wishType,
          tokenQuantity: offer?.tokenQuantity,
          maxPrice: offer?.maxPrice,
          currency: offer?.currency,
          description: offer?.description,
          specialConditions: offer?.specialConditions,
          exchangeWishes: offer?.exchangeWishes,
          category: offer?.category,
        },
      },
    });
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3500);
  };

  const tabOffers = allOffers?.filter((o) => o?.offerStatus === activeTab);
  const filteredOffers = applyFiltersAndSort(tabOffers, filters);

  const counts = {
    archived: allOffers?.filter((o) => o?.offerStatus === 'archived')?.length,
    expired: allOffers?.filter((o) => o?.offerStatus === 'expired')?.length,
    completed: allOffers?.filter((o) => o?.offerStatus === 'completed')?.length,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Archive size={22} className="text-teal-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Moji oglasi – Arhiva</h1>
              <p className="text-sm text-gray-500">Arhivirani, istekli i završeni oglasi</p>
            </div>
          </div>
        </div>

        {/* Success message */}
        {successMsg && (
          <div className="mb-4 flex items-center gap-2 p-3 bg-teal-50 border border-teal-200 rounded-lg text-teal-700 text-sm font-medium">
            <RefreshCw size={15} />
            {successMsg}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="mb-5">
          <ArchiveTabBar activeTab={activeTab} onTabChange={setActiveTab} counts={counts} />
        </div>

        {/* Filters */}
        <div className="mb-5">
          <ArchiveFilters filters={filters} onFiltersChange={setFilters} />
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Učitavanje arhive...</p>
            </div>
          </div>
        ) : filteredOffers?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <PackageOpen size={52} className="text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-1">
              {tabOffers?.length === 0 ? 'Nema oglasa u ovoj kategoriji' : 'Nema rezultata za zadatu pretragu'}
            </h3>
            <p className="text-sm text-gray-400 max-w-xs">
              {tabOffers?.length === 0
                ? activeTab === 'archived' ?'Ručno arhivirani oglasi će se pojaviti ovde.'
                  : activeTab === 'expired' ?'Oglasi kojima je istekao rok pojaviće se ovde.' :'Oglasi sa završenim pregovaranjima pojaviće se ovde.' :'Pokušajte sa drugačijim filterima ili pretragom.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredOffers?.map((offer) => (
              <ArchiveOfferCard
                key={offer?.id}
                offer={offer}
                onRestore={handleRestore}
                onRelist={handleRelist}
                onHistory={setHistoryOffer}
                isRestoring={restoringId === offer?.id}
                isRelisting={relistingId === offer?.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* History modal */}
      {historyOffer && (
        <HistoryModal offer={historyOffer} onClose={() => setHistoryOffer(null)} />
      )}
    </div>
  );
}
